import { useEffect, useMemo, useRef, type KeyboardEvent, type MouseEvent, type PointerEvent } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Badge } from '@/components/common/Badge/Badge';
import { Button } from '@/components/common/Button/Button';
import type { Candidate } from '@/models/candidate';
import { classNames } from '@/utils/classNames';
import { CandidateCard } from '../CandidateCard/CandidateCard';

import styles from '@/styles/home.module.css';

type CandidateSelectorProps = {
  candidates: Candidate[];
  selectedCandidateId: string;
  onSelectCandidate: (candidateId: string) => void;
  className?: string;
  loading?: boolean;
};

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  startScrollLeft: number;
  isDragging: boolean;
};

export function CandidateSelector({
  candidates,
  selectedCandidateId,
  onSelectCandidate,
  className,
  loading = false,
}: CandidateSelectorProps): JSX.Element | null {
  const orderedCandidates = useMemo(() => {
    return [...candidates].sort((left, right) => left.name.localeCompare(right.name, 'pt-BR'));
  }, [candidates]);

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const selectedItemRef = useRef<HTMLButtonElement | null>(null);
  const isLoopAdjustingRef = useRef(false);
  const dragStateRef = useRef<DragState | null>(null);
  const suppressClickRef = useRef(false);

  const selectedIndex = useMemo(() => {
    return orderedCandidates.findIndex((candidate) => candidate.id === selectedCandidateId);
  }, [orderedCandidates, selectedCandidateId]);

  const activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
  const canNavigate = orderedCandidates.length > 1;

  const carouselCandidates = useMemo(() => {
    if (orderedCandidates.length === 0) {
      return [];
    }

    return [...orderedCandidates, ...orderedCandidates, ...orderedCandidates].map((candidate, index) => ({
      candidate,
      key: `${candidate.id}-${Math.floor(index / orderedCandidates.length)}`,
      copyIndex: Math.floor(index / orderedCandidates.length),
    }));
  }, [orderedCandidates]);

  useEffect(() => {
    const selectedItem = selectedItemRef.current;

    if (selectedItem && typeof selectedItem.scrollIntoView === 'function') {
      selectedItem.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [selectedCandidateId, orderedCandidates]);

  if (loading) {
    return (
      <section className={className ?? styles.candidateSection} aria-label="Escolha um candidato" aria-busy="true">
        <div className={styles.candidateSectionHeader}>
          <div className={styles.candidateSectionHeading}>
            <p className={styles.candidateSectionTitle}>Escolha um candidato</p>
            <Badge tone="neutral">Ordem alfabética</Badge>
          </div>

          <div className={styles.candidateCarouselControls}>
            <span className={styles.candidateCarouselHint}>Deslize para navegar</span>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              className={styles.candidateCarouselControlButton}
              disabled
              ariaLabel="Selecionar candidato anterior"
            >
              <ChevronLeft size={16} />
            </Button>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              className={styles.candidateCarouselControlButton}
              disabled
              ariaLabel="Selecionar candidato seguinte"
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>

        <div className={styles.candidateCarousel} aria-hidden="true">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className={styles.candidateItem}>
              <div className={styles.candidateCard}>
                <div className={classNames(styles.skeleton, styles.candidateAvatarSkeleton)} />

                <div className={styles.candidateMeta}>
                  <div className={classNames(styles.skeleton, styles.candidateNameSkeleton)} />
                  <div className={classNames(styles.skeleton, styles.candidatePartySkeleton)} />
                  <div className={classNames(styles.skeleton, styles.candidateElectoralNumberSkeleton)} />
                  <div className={classNames(styles.skeleton, styles.candidateSelectedMarkSkeleton)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  const selectCandidateAtOffset = (offset: number): void => {
    if (!canNavigate) {
      return;
    }

    const nextIndex = (activeIndex + offset + orderedCandidates.length) % orderedCandidates.length;
    onSelectCandidate(orderedCandidates[nextIndex]!.id);
  };

  const handleScroll = (): void => {
    const scroller = scrollerRef.current;

    if (!scroller || isLoopAdjustingRef.current) {
      return;
    }

    const cycleWidth = scroller.scrollWidth / 3;

    if (!cycleWidth) {
      return;
    }

    const leftBoundary = cycleWidth * 0.35;
    const rightBoundary = cycleWidth * 2.65;

    if (scroller.scrollLeft < leftBoundary) {
      isLoopAdjustingRef.current = true;
      scroller.scrollLeft += cycleWidth;
      window.requestAnimationFrame(() => {
        isLoopAdjustingRef.current = false;
      });
      return;
    }

    if (scroller.scrollLeft > rightBoundary) {
      isLoopAdjustingRef.current = true;
      scroller.scrollLeft -= cycleWidth;
      window.requestAnimationFrame(() => {
        isLoopAdjustingRef.current = false;
      });
    }
  };

  const finishDrag = (pointerId: number): void => {
    const scroller = scrollerRef.current;

    if (scroller && 'hasPointerCapture' in scroller && scroller.hasPointerCapture(pointerId)) {
      scroller.releasePointerCapture(pointerId);
    }

    dragStateRef.current = null;
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.pointerType !== 'mouse' || event.button !== 0) {
      return;
    }

    const scroller = scrollerRef.current;

    if (!scroller) {
      return;
    }

    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startScrollLeft: scroller.scrollLeft,
      isDragging: false,
    };
    suppressClickRef.current = false;
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const state = dragStateRef.current;

    if (!state || state.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - state.startX;
    const deltaY = event.clientY - state.startY;
    const movedEnough = Math.hypot(deltaX, deltaY) > 6;

    if (!state.isDragging && movedEnough) {
      state.isDragging = true;
      suppressClickRef.current = true;

      const scroller = scrollerRef.current;

      if (scroller && 'setPointerCapture' in scroller) {
        scroller.setPointerCapture(event.pointerId);
      }
    }

    if (!state.isDragging) {
      return;
    }

    event.preventDefault();

    const scroller = scrollerRef.current;

    if (scroller) {
      scroller.scrollLeft = state.startScrollLeft - deltaX;
    }
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>): void => {
    if (dragStateRef.current?.pointerId === event.pointerId) {
      finishDrag(event.pointerId);
    }
  };

  const handlePointerCancel = (event: PointerEvent<HTMLDivElement>): void => {
    if (dragStateRef.current?.pointerId === event.pointerId) {
      finishDrag(event.pointerId);
    }
  };

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>): void => {
    if (!suppressClickRef.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    suppressClickRef.current = false;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      selectCandidateAtOffset(-1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      selectCandidateAtOffset(1);
    }
  };

  if (orderedCandidates.length === 0) {
    return null;
  }

  return (
    <section className={className ?? styles.candidateSection} aria-label="Escolha um candidato">
      <div className={styles.candidateSectionHeader}>
        <div className={styles.candidateSectionHeading}>
          <p className={styles.candidateSectionTitle}>Escolha um candidato</p>
          <Badge tone="neutral">Ordem alfabética</Badge>
        </div>

        <div className={styles.candidateCarouselControls}>
          <span className={styles.candidateCarouselHint}>Deslize para navegar</span>

          <Button
            type="button"
            variant="secondary"
            size="sm"
            className={styles.candidateCarouselControlButton}
            onClick={() => selectCandidateAtOffset(-1)}
            disabled={!canNavigate}
            ariaLabel="Selecionar candidato anterior"
          >
            <ChevronLeft size={16} />
          </Button>

          <Button
            type="button"
            variant="secondary"
            size="sm"
            className={styles.candidateCarouselControlButton}
            onClick={() => selectCandidateAtOffset(1)}
            disabled={!canNavigate}
            ariaLabel="Selecionar candidato seguinte"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className={styles.candidateCarousel}
        role="list"
        aria-label="Carrossel infinito de candidatos"
        tabIndex={0}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onClickCapture={handleClickCapture}
      >
        {carouselCandidates.map(({ candidate, key, copyIndex }) => (
          <div key={key} className={styles.candidateItem} role="listitem">
            <CandidateCard
              ref={copyIndex === 1 && candidate.id === selectedCandidateId ? selectedItemRef : undefined}
              candidate={candidate}
              selected={candidate.id === selectedCandidateId}
              onSelect={onSelectCandidate}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
